import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "className", "onSubmit"],
  _excluded2 = ["model", "field", "label", "help", "className", "iconLeft", "fill", "hidden", "noPadding", "buttonRight", "type", "offsetIcon"],
  _excluded3 = ["model", "field", "label", "help", "defaultValue", "className", "fill"],
  _excluded4 = ["model", "field", "label", "help", "className", "rows", "cols"],
  _excluded5 = ["model", "field", "label", "help", "className", "type"];
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { action } from 'mobx';
import moment from 'moment';
import cx from 'classnames';
import first from 'lodash/first';
import Icon from '../Icon';
import './styles.sass';
import DateTimeInput from '../DateTimeInput';
import { formatPhoneNumber } from '../../utils/phoneNumberValidationUtil';
import { formatSsn } from '../../utils/ssnValidationUtil';
import { formatEin } from '../../utils/einValidationUtil';

/**
 * Creates a submit handler that will invoke the
 * specified function after preventing defaults.
 */
var createSubmitHandler = function createSubmitHandler(onSubmit) {
  return function (e) {
    e.preventDefault();
    if (onSubmit) onSubmit(e);
  };
};

/**
 * Help block.
 */
var Help = function Help(_ref) {
  var help = _ref.help,
    error = _ref.error,
    className = _ref.className;
  return error || help ? /*#__PURE__*/React.createElement("span", {
    className: cx('help', error && 'is-danger', className)
  }, Array.isArray(error) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", null, error[0]), /*#__PURE__*/React.createElement("ul", {
    className: "error-list"
  }, error.slice(1).map(function (line, i) {
    return /*#__PURE__*/React.createElement("li", {
      key: i
    }, line);
  }))) || error || help) : null;
};
Help.propTypes = {
  help: PropTypes.node,
  error: PropTypes.node
};

/**
 * Form.
 */
var Form = function Form(_ref2) {
  var children = _ref2.children,
    className = _ref2.className,
    onSubmit = _ref2.onSubmit,
    opts = _objectWithoutProperties(_ref2, _excluded);
  return /*#__PURE__*/React.createElement("form", _extends({
    className: cx('Form', className)
  }, opts, {
    onSubmit: createSubmitHandler(onSubmit)
  }), children);
};
Form.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  onSubmit: PropTypes.func
};
Form = observer(Form);

/**
 * Helper to grab an error from a model.
 */
var getError = function getError(model, field) {
  if (!model) return undefined;
  if (!model.validation || !model.validation.errors) return undefined;
  var errors = model.validation.errors[field];
  if (errors) {
    return first(errors);
  }
  return undefined;
};

/**
 * Form group, wraps form components with proper spacing.
 */
var FormGroup = function FormGroup(_ref3) {
  var children = _ref3.children,
    model = _ref3.model,
    error = _ref3.error,
    help = _ref3.help,
    className = _ref3.className,
    label = _ref3.label,
    style = _ref3.style;
  return /*#__PURE__*/React.createElement("div", {
    className: cx('FormGroup', className),
    style: style
  }, label && /*#__PURE__*/React.createElement("label", {
    className: "formGrouplabel"
  }, label), children, /*#__PURE__*/React.createElement(Help, {
    error: getError(model, error),
    help: help
  }));
};
FormGroup.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  label: PropTypes.node
};

/**
 * Wrap an input if it has content to the right
 */
var InputGroup = function InputGroup(_ref4) {
  var contentRight = _ref4.contentRight,
    statusClass = _ref4.statusClass,
    children = _ref4.children;
  if (contentRight) {
    return /*#__PURE__*/React.createElement("div", {
      className: cx('InputGroup', statusClass)
    }, children, contentRight);
  }
  return children;
};

/**
 * Input field component.
 */
var Input = function Input(_ref5) {
  var model = _ref5.model,
    field = _ref5.field,
    label = _ref5.label,
    help = _ref5.help,
    className = _ref5.className,
    _ref5$iconLeft = _ref5.iconLeft,
    iconLeft = _ref5$iconLeft === void 0 ? null : _ref5$iconLeft,
    _ref5$fill = _ref5.fill,
    fill = _ref5$fill === void 0 ? false : _ref5$fill,
    _ref5$hidden = _ref5.hidden,
    hidden = _ref5$hidden === void 0 ? false : _ref5$hidden,
    _ref5$noPadding = _ref5.noPadding,
    noPadding = _ref5$noPadding === void 0 ? false : _ref5$noPadding,
    _ref5$buttonRight = _ref5.buttonRight,
    buttonRight = _ref5$buttonRight === void 0 ? null : _ref5$buttonRight,
    _ref5$type = _ref5.type,
    type = _ref5$type === void 0 ? 'text' : _ref5$type,
    _ref5$offsetIcon = _ref5.offsetIcon,
    offsetIcon = _ref5$offsetIcon === void 0 ? true : _ref5$offsetIcon,
    props = _objectWithoutProperties(_ref5, _excluded2);
  var error = getError(model, field);
  if (props.type === 'checkbox') {
    return /*#__PURE__*/React.createElement(FormGroup, {
      className: "Checkbox"
    }, /*#__PURE__*/React.createElement("label", {
      className: "inputLabel"
    }, /*#__PURE__*/React.createElement("input", _extends({
      type: "checkbox",
      className: cx('checkbox', error && 'is-danger', className)
    }, model && {
      checked: model[field] === undefined ? false : model[field],
      onChange: action(function (e) {
        model[field] = e.target.checked;
      }),
      disabled: model.disabled
    }, props)), "\xA0", label), /*#__PURE__*/React.createElement(Help, {
      error: error,
      help: help
    }));
  }
  var isSecureField = type === 'password' || type === 'ssn' || type === 'ein' || hidden;
  var _useState = useState(isSecureField && !!model[field] ? 'password' : type),
    _useState2 = _slicedToArray(_useState, 2),
    visibilityType = _useState2[0],
    setVisibilityType = _useState2[1];
  var icon = visibilityType === 'password' ? 'visibility' : 'visibility off';
  var togglePasswordVisibility = function togglePasswordVisibility() {
    setVisibilityType(function (prevType) {
      return prevType === 'password' ? 'text' : 'password';
    });
  };
  function getAdjustedCursorPosition(rawValue, formattedValue, cursorPosition) {
    var rawIndex = 0;
    var formattedIndex = 0;
    if (cursorPosition === 0) {
      return 0;
    }
    while (rawIndex < rawValue.length && formattedIndex < formattedValue.length) {
      if (formattedValue[formattedIndex] === rawValue[rawIndex]) {
        rawIndex++;
      }
      formattedIndex++;
      if (rawIndex === cursorPosition) {
        break;
      }
    }
    return formattedIndex;
  }
  return /*#__PURE__*/React.createElement(FormGroup, {
    className: "Input",
    style: {
      padding: noPadding && 0,
      width: fill && '100%'
    }
  }, label && /*#__PURE__*/React.createElement("label", {
    className: "inputLabel"
  }, label, isSecureField && /*#__PURE__*/React.createElement("a", {
    className: "PasswordVisibility",
    onClick: togglePasswordVisibility
  }, /*#__PURE__*/React.createElement(Icon, {
    material: true,
    name: icon,
    className: cx('icon', offsetIcon && 'offsetIcon')
  }), visibilityType === 'password' ? ' Show' : ' Hide')), /*#__PURE__*/React.createElement("div", {
    className: "control"
  }, iconLeft && /*#__PURE__*/React.createElement(Icon, {
    material: true,
    name: iconLeft
  }), /*#__PURE__*/React.createElement(InputGroup, {
    contentRight: buttonRight,
    statusClass: error && 'is-danger'
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: visibilityType,
    className: cx('input', error && 'is-danger', className, iconLeft && 'iconLeft', visibilityType === 'date' && 'date-input')
  }, model && {
    value: formatValueByType(model[field], type),
    onChange: action(function (e) {
      var input = e.target;
      var rawValue = input.value;
      var formattedValue = formatValueByType(rawValue, type);

      // Update the model with the raw value
      model[field] = formattedValue;

      // Adjust the cursor position for certain types
      if (['tel', 'ssn', 'ein'].includes(type)) {
        var cursorPosition = input.selectionStart;
        var newCursorPosition = getAdjustedCursorPosition(rawValue, formattedValue !== null && formattedValue !== void 0 ? formattedValue : rawValue, cursorPosition);
        window.requestAnimationFrame(function () {
          input.setSelectionRange(newCursorPosition, newCursorPosition);
        });
      }
    }),
    disabled: model.disabled
  }, props))), /*#__PURE__*/React.createElement(Help, {
    error: error,
    help: help
  }), type === 'password' && /*#__PURE__*/React.createElement(PasswordStrengthLabel, {
    input: model[field]
  })));
};
var PasswordStrengthLabel = function PasswordStrengthLabel(_ref6) {
  var input = _ref6.input;
  if (!input) {
    return /*#__PURE__*/React.createElement("div", null);
  }
  var strength = getPasswordStrength(input);
  return /*#__PURE__*/React.createElement("div", {
    className: "StrengthContainer"
  }, 'Password strength: ', /*#__PURE__*/React.createElement("span", {
    className: strength
  }, strength));
};
function getPasswordStrength(value) {
  var regex = {
    lowercase: /[a-z]/,
    uppercase: /[A-Z]/,
    numeric: /[0-9]/,
    specialChars: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/
  };
  var strength = 0;
  if (value.length > 8) {
    strength += 1;
  }

  // For each regex item determine the password's strength.
  Object.values(regex).forEach(function (pattern) {
    if (pattern.test(value)) {
      strength += 1;
    }
  });
  return strength < 3 ? 'Weak' : strength < 5 ? 'Medium' : 'Strong';
}
function formatValueByType(value, type) {
  if (value === undefined) {
    return '';
  }
  if (type === 'tel') {
    return formatPhoneNumber(value);
  }
  if (type === 'ssn') {
    return formatSsn(value);
  }
  if (type === 'ein') {
    return formatEin(value);
  }
  if (type === 'numeric') {
    return value.replace(/[^\d]/g, '');
  }
  return value === undefined ? '' : value;
}
Input.propTypes = {
  model: PropTypes.object,
  field: PropTypes.string,
  label: PropTypes.node,
  help: PropTypes.node,
  className: PropTypes.string,
  type: PropTypes.string,
  dataTest: PropTypes.string
};
Input = observer(Input);

/**
 * Select field component.
 */
var Select = function Select(_ref7) {
  var model = _ref7.model,
    field = _ref7.field,
    label = _ref7.label,
    help = _ref7.help,
    defaultValue = _ref7.defaultValue,
    className = _ref7.className,
    _ref7$fill = _ref7.fill,
    fill = _ref7$fill === void 0 ? false : _ref7$fill,
    props = _objectWithoutProperties(_ref7, _excluded3);
  var error = getError(model, field);
  return /*#__PURE__*/React.createElement(FormGroup, {
    className: "Select",
    style: {
      width: fill && '100%'
    }
  }, /*#__PURE__*/React.createElement("label", {
    className: "inputLabel"
  }, label), /*#__PURE__*/React.createElement("div", {
    className: "control"
  }, /*#__PURE__*/React.createElement("span", {
    className: cx('select', error && 'is-danger', className)
  }, /*#__PURE__*/React.createElement("select", _extends({}, model && {
    value: model[field] === undefined ? '' : model[field],
    onChange: action(function (e) {
      model[field] = e.target.value;
    }),
    disabled: model.disabled
  }, props))), /*#__PURE__*/React.createElement(Help, {
    error: error,
    help: help
  })));
};
Select.propTypes = {
  model: PropTypes.object,
  field: PropTypes.string,
  label: PropTypes.node,
  help: PropTypes.node,
  className: PropTypes.string
};
Select = observer(Select);

/**
 *
 * Text area component
 */
var TextArea = function TextArea(_ref8) {
  var model = _ref8.model,
    field = _ref8.field,
    label = _ref8.label,
    help = _ref8.help,
    className = _ref8.className,
    rows = _ref8.rows,
    cols = _ref8.cols,
    props = _objectWithoutProperties(_ref8, _excluded4);
  var error = getError(model, field);
  return /*#__PURE__*/React.createElement(FormGroup, {
    className: "Input",
    label: label
  }, /*#__PURE__*/React.createElement("div", {
    className: "control"
  }, /*#__PURE__*/React.createElement("textarea", _extends({
    rows: rows,
    cols: cols,
    className: cx('input', error && 'is-danger', className)
  }, model && {
    value: model[field] === undefined ? '' : model[field],
    onChange: action(function (e) {
      model[field] = e.target.value;
    }),
    disabled: model.disabled
  }, props)), /*#__PURE__*/React.createElement(Help, {
    error: error,
    help: help
  })));
};
TextArea.propTypes = {
  model: PropTypes.object,
  field: PropTypes.string,
  label: PropTypes.node,
  help: PropTypes.node,
  className: PropTypes.string,
  rows: PropTypes.number,
  cols: PropTypes.number
};
TextArea = observer(TextArea);

/**
 * Date picker component.
 */
var DatePicker = function DatePicker(_ref9) {
  var model = _ref9.model,
    field = _ref9.field,
    label = _ref9.label,
    help = _ref9.help,
    className = _ref9.className,
    _ref9$type = _ref9.type,
    type = _ref9$type === void 0 ? 'date' : _ref9$type,
    props = _objectWithoutProperties(_ref9, _excluded5);
  var error = getError(model, field);
  var val = !model[field] ? null : model[field].toDate();
  return /*#__PURE__*/React.createElement(FormGroup, {
    className: "Input Datepicker",
    label: label
  }, /*#__PURE__*/React.createElement("div", {
    className: "control"
  }, /*#__PURE__*/React.createElement(DateTimeInput, _extends({
    type: type,
    className: cx('input', error && 'is-danger', className),
    value: val,
    onChange: action(function (e) {
      model[field] = moment(e);
    }),
    disabled: model.disabled
  }, props, {
    "data-test": props.dataTest
  })), /*#__PURE__*/React.createElement(Help, {
    error: error,
    help: help
  })));
};
DatePicker.propTypes = {
  model: PropTypes.object,
  field: PropTypes.string,
  label: PropTypes.node,
  help: PropTypes.node,
  className: PropTypes.string,
  type: PropTypes.string
};
DatePicker = observer(DatePicker);
export { Form, FormGroup, Help, Input, DatePicker, Select, getError, TextArea };
//# sourceMappingURL=index.js.map
